import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { LogIn, Loader2 } from 'lucide-react';

const Login = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [loading, setLoading] = useState(false);
  const [welcome, setWelcome] = useState(null); // { name, branch }
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const result = await login(formData.email, formData.password);

    if (result.success) {
      const u = result.user || {};
      const displayName = u.name || u.full_name || (u.email || '').split('@')[0] || 'there';
      const branchLine = u.branch_name || (u.role === 'Admin' ? 'Headquarters' : '');
      setWelcome({ name: displayName, branch: branchLine, role: u.role });
      toast.success(`Welcome, ${displayName}!`);
      // Auto-dismiss the welcome dialog and route to dashboard after 2s
      setTimeout(() => {
        setWelcome(null);
        navigate('/');
      }, 2000);
    } else {
      toast.error(result.error || 'Login failed');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left side - Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-white">
        <div className="w-full max-w-md space-y-8">
          <div className="text-center">
            <h1 className="text-4xl font-bold text-emerald-700 mb-2" data-testid="login-title">
              WWAGS MIS Portal
            </h1>
            <p className="text-slate-600">Business Resource Center Management</p>
          </div>

          <div className="bg-slate-50 rounded-xl border border-slate-200 p-8">
            <div className="mb-6">
              <h2 className="text-2xl font-semibold text-slate-900">Welcome Back</h2>
              <p className="text-sm text-slate-500 mt-1">Sign in to your branch account</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6" data-testid="login-form">
              <div className="space-y-2">
                <Label htmlFor="email">Email Address</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="branch@wwags.org"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  data-testid="login-email-input"
                  className="h-11"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="Enter your password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required
                  data-testid="login-password-input"
                  className="h-11"
                />
              </div>

              <Button
                type="submit"
                disabled={loading}
                data-testid="login-submit-button"
                className="w-full h-11 bg-emerald-700 hover:bg-emerald-800 text-white font-medium"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Signing In...
                  </>
                ) : (
                  <>
                    <LogIn className="w-4 h-4 mr-2" />
                    Sign In
                  </>
                )}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <p className="text-xs text-slate-500">
                Contact your administrator to create an account
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Right side - Image */}
      <div
        className="hidden lg:flex flex-1 bg-cover bg-center relative"
        style={{
          backgroundImage: 'url(https://images.pexels.com/photos/8902342/pexels-photo-8902342.jpeg)',
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-emerald-900/80 to-orange-900/60"></div>
        <div className="relative z-10 flex flex-col justify-center p-12 text-white">
          <h2 className="text-5xl font-bold mb-6">Empowering Women Entrepreneurs</h2>
          <p className="text-xl text-slate-100 leading-relaxed max-w-xl">
            Supporting women-led enterprises through structured business resource management
            and comprehensive financial tracking.
          </p>
        </div>
      </div>
      {/* Welcome popup after successful login */}
      {welcome && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 backdrop-blur-sm animate-in fade-in duration-300" data-testid="welcome-popup">
          <div className="bg-white rounded-2xl shadow-2xl px-10 py-8 max-w-md mx-4 text-center border-2 border-emerald-200 animate-in zoom-in-95 duration-500">
            <div className="mx-auto w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mb-4 text-4xl">👋</div>
            <h2 className="text-2xl font-bold text-emerald-800 mb-1">Welcome, {welcome.name}!</h2>
            {welcome.branch && (
              <p className="text-sm text-slate-600 mt-1">
                {welcome.role === 'Admin' ? '' : 'Branch: '}
                <span className="font-semibold text-slate-800">{welcome.branch}</span>
              </p>
            )}
            <p className="text-xs text-slate-400 mt-3 italic">Redirecting you to the dashboard…</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default Login;